import { ListAllVehicule, TotalActiveVehicule } from '@/api/entities/vehicule';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createNewVehicule,
  getAllVehicules,
  getTotalActiveVehicules,
  getVehiculeById,
  listAllVehicules,
  updateManyVehicules,
  updateVehicule,
  importVehicule,
  getMatchingVehiculeError,
  getExistingVehicule,
  deleteVehicule,
  deleteManyVehicules,
} from '@/api/vehicule/vehicule';
import { VehiculeCheckFilterType } from '@/entities/vehicle';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';
import { useAuth } from './auth.hooks';
import { useListAllCompanies } from './company.hooks';

interface RequestVehiculesProps<TSchema> {
  data: TSchema[];
}

interface VehiculeIsToAddProps {
  vehicule: TVehiculeSchema;
  selectedOwnerGroupId?: string;
  selectedOwnerCompanyId?: string;
}

export const useCreateVehicule = ({
  t,
  showDefaultSuccessMessage = true,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TVehiculeSchema) =>
      createNewVehicule(data, {
        createdByUserId: limitedRoleLoggedInUserId,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });

      if (showDefaultSuccessMessage) {
        notify(t('common:vehiculeCreatedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingVehiculeError = (query?: TVehiculeSchema) => {
  return useQuery({
    queryKey: ['getMatchingVehiculeError', query],
    queryFn: () => getMatchingVehiculeError(query),
  });
};

export const useGetExistingVehicule = (query?: TVehiculeSchema) => {
  return useQuery({
    queryKey: ['getExistingVehicule', query],
    queryFn: () => getExistingVehicule(query),
  });
};

export const useListAllVehicules = (query?: ListAllVehicule) => {
  const { limitedRoleLoggedInUserId } = useAuth();

  return useQuery({
    queryKey: ['listAllVehicules', query],
    queryFn: () =>
      listAllVehicules({ ...query, userId: limitedRoleLoggedInUserId }),
  });
};

export const useGetAllVehicules = (
  filter: VehiculeCheckFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllVehicules', filter, page, sortBy, sortDirection],
    queryFn: () => getAllVehicules(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveVehicules = (query?: TotalActiveVehicule) => {
  return useQuery({
    queryKey: ['getTotalActiveVehicules', query],
    queryFn: () => getTotalActiveVehicules(query),
  });
};

export const useGetVehiculeById = (id: string) => {
  return useQuery({
    queryKey: ['getVehiculeById', id],
    queryFn: () => getVehiculeById(id),
    enabled: !!id,
  });
};

export const useVehiculeIsToAdd = ({
  vehicule,
  selectedOwnerGroupId,
  selectedOwnerCompanyId,
}: VehiculeIsToAddProps) => {
  const { data: allCompanies, isLoading: isLoadingAllCompanies } =
    useListAllCompanies();

  const ownerChanged =
    (vehicule?.ownerGroupId || null) !== (selectedOwnerGroupId || null) ||
    (vehicule?.ownerCompanyId || null) !== (selectedOwnerCompanyId || null);

  // A group and a company are "related" when the company belongs to the group
  // (Company.groupId === groupId). Reassigning between related owners is an
  // update on the same vehicle; only unrelated reassignments duplicate it.
  const findCompanyGroupId = (companyId?: string | null) =>
    allCompanies?.data?.find(
      (c: { id: string; groupId?: string | null }) => c?.id === companyId,
    )?.groupId ?? null;

  const ownersRelated =
    (!!vehicule?.ownerGroupId &&
      !!selectedOwnerCompanyId &&
      findCompanyGroupId(selectedOwnerCompanyId) === vehicule?.ownerGroupId) ||
    (!!vehicule?.ownerCompanyId &&
      !!selectedOwnerGroupId &&
      findCompanyGroupId(vehicule?.ownerCompanyId) === selectedOwnerGroupId);

  const vehiculeIsToAdd =
    vehicule &&
    (vehicule?.ownerGroupId || vehicule?.ownerCompanyId) &&
    (selectedOwnerGroupId || selectedOwnerCompanyId) &&
    ownerChanged &&
    !ownersRelated;

  return {
    vehiculeIsToAdd,
    isLoadingVehiculeCompanies: isLoadingAllCompanies,
  };
};

export const useUpdateVehicule = ({
  t,
  showDefaultSuccessMessage = true,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehicule: TVehiculeSchema) => updateVehicule(vehicule),
    onSuccess: (data, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeById', id] });

      if (showDefaultSuccessMessage) {
        notify(t('common:vehiculeUpdatedSuccessfully'), NOTIFY.SUCCESS);
      }

      handleSuccess?.(data);
    },
    onError: (err: ErrorResponse) => {
      handleError?.(err);
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicule:', err.error);
    },
  });
};

export const useImportVehicles = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vehicule: TVehiculeSchema[]) => importVehicule(vehicule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });
      queryClient.invalidateQueries({ queryKey: ['getVehiculeById'] });

      handleSuccess?.();

      notify(t('vehiculeImportSuccess'), NOTIFY.SUCCESS);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicules:', error);
    },
  });
};

export const useUpdateManyVehicules = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vehicules: TVehiculeSchema[]) =>
      updateManyVehicules(vehicules),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getVehiculeById', item?.id],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });

      notify(
        variables.length === 0
          ? t('common:theseElementsLinkedToEntitiesNotDeactivable')
          : variables.length > 1
            ? t('vehiculesUpdatedSuccessfully')
            : t('vehiculeUpdatedSuccessfully'),
        NOTIFY.SUCCESS,
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating vehicules:', error);
    },
  });
};

export const useGetVehiculesSelectOptions = (query?: ListAllVehicule) => {
  const { data: allVehicules, isLoading: isLoadingAllVehicules } =
    useListAllVehicules(query);

  const getAllVehiculesSelectOptions = () => {
    if (
      !isLoadingAllVehicules &&
      allVehicules &&
      allVehicules.data.length > 0
    ) {
      return allVehicules.data.map((group: TVehiculeSchema) => {
        const { id, brand, model } = group;
        const licensePlateNumber = group?.licensePlateNumber
          ? `${group?.licensePlateNumber} -`
          : '';
        const vin = group?.vin ? ` - ${group?.vin}` : '';

        return {
          label: `${licensePlateNumber} ${brand || ''} - ${model || ''} ${vin}`,
          value: id,
        };
      });
    }

    return undefined;
  };

  return {
    getAllVehiculesSelectOptions,
    isLoadingAllVehicules,
  };
};

export const useRequestVehicules = <TSchema extends FieldValues>({
  data,
}: RequestVehiculesProps<TSchema>) => {
  const [activateAllVehiculeRequests, setActivateAllVehiculeRequests] =
    useState(false);

  const [requestedVehicules, setRequestedVehicules] = useState<TSchema[]>([]);

  const { mutate: mutateVehicules, isPending: isPendingVehicules } =
    useUpdateManyVehicules();

  useEffect(() => {
    const filteredData = data?.filter((item) => item?.requestVehiculeCheck);

    if (filteredData?.length === data?.length) {
      setActivateAllVehiculeRequests(true);
    } else {
      setActivateAllVehiculeRequests(false);
    }

    setRequestedVehicules(filteredData);
  }, [data]);

  const handleModalConfirmRequestVehicules = () => {
    let itemList = [];

    if (activateAllVehiculeRequests) {
      itemList = data?.map((item) => ({
        ...item,
        requestVehiculeCheck: true,
      }));
    } else {
      itemList = data?.map((item) => ({
        ...item,
        requestVehiculeCheck: false,
      }));
    }

    setRequestedVehicules(itemList);
    mutateVehicules(itemList);
  };

  const handleRequestVehicule = (entry: TSchema) => {
    let itemList: TSchema[] = requestedVehicules;

    const existingItem = itemList?.find((item) => item?.id === entry?.id);

    if (existingItem) {
      itemList = itemList?.filter((item) => item?.id !== entry?.id);

      mutateVehicules([
        {
          ...existingItem,
          requestVehiculeCheck: false,
        },
      ]);
    } else {
      itemList = [
        ...itemList,
        {
          ...entry,
          requestVehiculeCheck: true,
        },
      ];

      mutateVehicules(itemList);
    }

    setRequestedVehicules(itemList);
  };

  return {
    activateAllVehiculeRequests,
    requestedVehicules,
    isPendingVehicules,
    setActivateAllVehiculeRequests,
    handleModalConfirmRequestVehicules,
    handleRequestVehicule,
  };
};

export const useDeleteVehicule = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteVehicule(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });

      notify(t('vehiculeDeletedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.({ id });
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useDeleteManyVehicules = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => deleteManyVehicules(ids),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['getAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['listAllVehicules'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingVehiculeError'] });

      notify(
        ids.length === 0
          ? t('common:theseElementsLinkedToEntitiesNotDeletable')
          : ids.length > 1
            ? t('vehiculesDeletedSuccessfully')
            : t('vehiculeDeletedSuccessfully'),
        NOTIFY.SUCCESS,
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};
